import { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useIsFirstRender } from 'usehooks-ts';

import { generateRangeFromType } from 'utils/dateRange';
import dayjs, { DATE_FORMAT } from 'utils/dayjs';

import {
  selectDateRange,
  setCustomRange,
  setDateRangeType
} from 'reduxLocal/slices/globalFiltersSlice';

import type { DateRangeType } from 'models/dateRange';

interface GlobalQueryParams {
  'dateRange.type'?: string;
  'dateRange.from'?: string;
  'dateRange.to'?: string;
}

export const useGlobalFilters = () => {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const isFirst = useIsFirstRender();

  const dateRange = useSelector(selectDateRange, shallowEqual);

  const queryParams = useMemo<GlobalQueryParams>(() => {
    const range = generateRangeFromType(dateRange);
    return {
      'dateRange.type': range.type,
      'dateRange.from': range.from,
      'dateRange.to': range.to
    };
  }, [dateRange]);

  const filters = useMemo(() => ({ dateRange }), [dateRange]);

  if (isFirst) {
    const dateRangeType = searchParams.get('dateRange.type') as DateRangeType;

    if (dateRangeType) {
      if (dateRangeType != 'custom') {
        dispatch(setDateRangeType(dateRangeType));
      } else {
        const from = dayjs(
          searchParams.get('dateRange.from'),
          DATE_FORMAT
        ).format(DATE_FORMAT);
        const to = dayjs(searchParams.get('dateRange.to'), DATE_FORMAT).format(
          DATE_FORMAT
        );
        dispatch(setCustomRange({ from, to }));
      }
    }
  }

  const setDateRange = (params: DateRangeParams) => {
    if (params.type == 'custom') {
      dispatch(setCustomRange({ from: params.from, to: params.to }));
    } else {
      dispatch(setDateRangeType(params.type));
    }
  };

  return {
    filters,
    setDateRange,
    queryParams
  };
};

export type DateRangeParams =
  | {
      type: Exclude<DateRangeType, 'custom'>;
    }
  | {
      type: 'custom';
      to: string;
      from: string;
    };
